// @givmo/fundraisers-module — every line the Messages tab shows (FCM-WEB50; plan §FC-17.5), as Jonathan
// ruled them with mockup M4 on 2026-10-23 07:07 PDT (D-FCM-64, "good": all seven decisions as drawn,
// with their NEW lines), `scratchpad/2026-10-22-fcm-web50-web52-messages-mockups/`, copied from that set's
// copy list character for character. Each line says what it is there: RULED, REUSED or NEW (ruled with
// the set).
//
// SERVED words are the backend's, never copied here: every refusal is the server's own `detail` (D-FCM-60's
// ruled sentences), and the preview is the server's own render of the email. The line above the composer
// and the line over Givmo's two notes are each class's own words, so they are host data
// (`FundraisersFlavor.messagesNote`, `messagesNotesLine`, set in each console's mount).
//
// ⚠ The module's copy rail reads this file's FULL text, comments included (`copy-law.test.ts`): a message
// that went out is "Delivered" and "on its way", never the truth-gated word.

/** "42 donors", and "1 donor" at one: the module's counted-noun rule. */
function donors(n: number): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'donor' : 'donors'}`;
}

export const MESSAGES_COPY = {
  /** NEW (decision 1): the tab, third after Details and Donors, and its unavailable panel's title. */
  tab: 'Messages',
  /** NEW: the composer card's title. */
  composerTitle: 'Message your donors',
  /** NEW: the box's label. */
  label: 'Your message',
  /** SERVED shape (§FC-17.4, the email's own line): "From {signed name}", over the box, on each history
   *  row and in the email. The name is the server's signature: the organization's own name. */
  from: (signedName: string) => `From ${signedName}`,
  /** RULED: §FC-17.5 ("One editable “Say thank you” starter"). */
  starterAction: 'Say thank you',
  /** NEW: the starter's words (written to pass the server's guard and this rail). */
  starter:
    'Thank you for your gift. Your generosity means so much to us and to everyone this fundraiser helps. We’re grateful you were part of it.',
  /** NEW format, D-FCM-60's cap: "135 / 2,000", counted as the server counts (`messageLength`). */
  count: (n: number, cap: number) =>
    `${n.toLocaleString('en-US')} / ${cap.toLocaleString('en-US')}`,
  /** REUSED: the Campaign tab's card preview (`layers-copy.ts`, `stylePreview`). */
  preview: 'Preview',
  /** NEW: the plan's own example (§FC-17.2: “Goes to 42 donors”). A count, never a name. */
  goesTo: (n: number) => `Goes to ${donors(n)}`,
  /** NEW labels over the dry run's own subject and sender. */
  subject: 'Subject',
  fromLabel: 'From',
  /** NEW: back from the preview to the box, the words kept. */
  edit: 'Edit',
  /** NEW: the preview's act, in the ruled confirmation's words. */
  sendTo: (n: number) => `Send to ${donors(n)}`,
  /** RULED: §FC-17.5 and the FCM-WEB50 line, “Send to {N} donors? This can't be undone.”, with the
   *  house's typographic apostrophe (FCM-BE105). */
  confirmTitle: (n: number) => `Send to ${donors(n)}?`,
  confirmBody: 'This can’t be undone.',
  /** NEW: the confirmation's act (its Cancel is the module dialog's own). */
  confirm: 'Send',
  /** NEW: once the server took the message; its batches go out over the next minutes. */
  onItsWay: (n: number) => `Message on its way to ${donors(n)}.`,
  /** NEW (decision 4): after the weekly limit's sentence; the date is the server's `next_send_at`. */
  nextSend: (when: string) => `You can send the next one on ${when}.`,
  /** NEW, the module's pattern (“Couldn’t answer this request. Please try again.”): a failed preview or
   *  send that is no refusal. */
  previewFailed: 'Couldn’t preview this message. Please try again.',
  sendFailed: 'Couldn’t send this message. Please try again.',
  /** NEW: the history card's title. */
  historyTitle: 'Messages to donors',
  /** NEW (decision 3): what each count is, the audience fixed when the message went out (P4 of cut
   *  `fc-donor-messages-1022`'s review). */
  countLine: 'Each count is the donors a message was addressed to when it went out.',
  /** RULED: §FC-17.5 and the FCM-WEB50 line, “Delivered {date} · {N} donors”. */
  delivered: (date: string, n: number) => `Delivered ${date} · ${donors(n)}`,
  /** NEW: a message Givmo took down (`removed_by_givmo`); it left every inbox. */
  removed: 'Removed by Givmo',
  /** NEW: the module's “No … yet” pattern (`donors-copy.ts`). */
  empty: 'No messages yet',
  /** NEW: the module's pattern (`donors-copy.ts`, “Couldn’t load donations”). */
  failed: 'Couldn’t load messages',
  /** REUSED: the module's failure body and act (`fundraisers-state.tsx`). */
  failedBody: 'Please try again.',
  retry: 'Try again',
  /** NEW: the tab's own address while the detail lights no composer (the Donors tab's pattern). */
  unavailable: 'Messages to donors aren’t available for this fundraiser here.',
  /** REUSED: the module's paging (`donors-copy.ts`, D-FCM-55's ruled pager), past 25 messages. */
  previous: 'Previous',
  page: (n: number) => `Page ${n}`,
  next: 'Next',
} as const;

/** D-FCM-60's cap on a message, as the server's guard holds it (`MESSAGE_MAX_CHARS`). */
export const MESSAGE_CAP = 2000;
